import { useState } from 'react';
import type { AiConversation } from '@accounting/shared';

interface Props {
  conversations: AiConversation[];
  loading: boolean;
  activeId: string | null;
  archived: boolean;
  onSetArchived: (v: boolean) => void;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onArchive: (id: string, value: boolean) => void;
  onDelete: (id: string) => void;
}

function relativeDate(iso: string): string {
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  if (days <= 0) return then.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Saved threads: pick one up where you left it, rename, archive or delete. */
export function ConversationList({
  conversations,
  loading,
  activeId,
  archived,
  onSetArchived,
  onSelect,
  onNew,
  onRename,
  onArchive,
  onDelete,
}: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  function beginRename(c: AiConversation) {
    setEditingId(c.id);
    setDraft(c.title);
  }

  function commitRename(id: string) {
    const title = draft.trim();
    if (title) onRename(id, title);
    setEditingId(null);
  }

  return (
    <aside
      className="card"
      style={{
        width: 260,
        flexShrink: 0,
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        overflow: 'hidden',
      }}
    >
      <button type="button" className="btn-primary" onClick={onNew} style={{ width: '100%' }}>
        + New chat
      </button>

      <div style={{ display: 'flex', gap: 4 }}>
        <button
          type="button"
          className={archived ? 'btn-secondary' : 'btn-primary'}
          style={{ flex: 1, fontSize: 12, padding: '4px 6px' }}
          onClick={() => onSetArchived(false)}
        >
          Active
        </button>
        <button
          type="button"
          className={archived ? 'btn-primary' : 'btn-secondary'}
          style={{ flex: 1, fontSize: 12, padding: '4px 6px' }}
          onClick={() => onSetArchived(true)}
        >
          Archived
        </button>
      </div>

      <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading && <p className="muted" style={{ fontSize: 12 }}>Loading…</p>}
        {!loading && conversations.length === 0 && (
          <p className="muted" style={{ fontSize: 12, padding: '8px 4px' }}>
            {archived ? 'Nothing archived.' : 'No saved chats yet.'}
          </p>
        )}

        {conversations.map((c) => {
          const active = c.id === activeId;
          return (
            <div
              key={c.id}
              style={{
                borderRadius: 8,
                padding: '6px 8px',
                background: active ? 'var(--bg-surface-2)' : 'transparent',
                borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
              }}
            >
              {editingId === c.id ? (
                <input
                  autoFocus
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => commitRename(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename(c.id);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  style={{ width: '100%', fontSize: 13 }}
                />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => onSelect(c.id)}
                    title={c.title}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      display: 'block',
                      width: '100%',
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.title}
                  </button>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 4,
                    }}
                  >
                    <span className="muted" style={{ fontSize: 11 }}>
                      {relativeDate(c.updatedAt)}
                      {c.messageCount ? ` · ${c.messageCount} msg` : ''}
                    </span>
                    <span style={{ display: 'flex', gap: 2 }}>
                      <IconButton label="Rename" onClick={() => beginRename(c)}>
                        ✎
                      </IconButton>
                      <IconButton
                        label={archived ? 'Unarchive' : 'Archive'}
                        onClick={() => onArchive(c.id, !archived)}
                      >
                        {archived ? '↩' : '🗄'}
                      </IconButton>
                      <IconButton
                        label="Delete"
                        onClick={() => {
                          if (window.confirm(`Delete "${c.title}"? This cannot be undone.`)) {
                            onDelete(c.id);
                          }
                        }}
                      >
                        ✕
                      </IconButton>
                    </span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function IconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className="muted"
      style={{
        all: 'unset',
        cursor: 'pointer',
        fontSize: 11,
        padding: '0 3px',
        borderRadius: 4,
        lineHeight: 1.4,
      }}
    >
      {children}
    </button>
  );
}
